import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import BrandMark from '../components/BrandMark';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import FacebookIcon from '@mui/icons-material/Facebook';
import { useLogin, useRegister, useSocialLogin } from '../hooks/useAuth';
import { isAuthenticated } from '../services/apiClient';
import {
  enabledSocialProviders,
  loadSocialSdk,
  requestSocialCredential,
  SocialLoginCancelled,
  SocialLoginError,
} from '../services/socialAuth';
import { SocialProvider } from '../types/api';
import { getApiErrorMessage } from '../utils/errors';
import { BRAND } from '../theme';

const FEATURES = [
  { icon: <ChecklistRoundedIcon />, title: 'Organize tasks', text: 'Prioritize, categorize and never miss a due date.' },
  { icon: <ScheduleRoundedIcon />, title: 'Track your time', text: 'Log hours against tasks and see where your day goes.' },
  { icon: <AutoAwesomeRoundedIcon />, title: 'AI assistant', text: 'Manage everything by simply asking in plain language.' },
];

// Google's multicolor "G" (its branding guidelines ask for this mark).
const GoogleIcon: React.FC = () => (
  <Box component="svg" viewBox="0 0 48 48" sx={{ width: 20, height: 20 }} aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </Box>
);

const SOCIAL_BUTTONS: Record<SocialProvider, { label: string; icon: React.ReactNode }> = {
  google: { label: 'Google', icon: <GoogleIcon /> },
  facebook: { label: 'Facebook', icon: <FacebookIcon sx={{ color: '#1877F2' }} /> },
};

type Mode = 'signin' | 'signup';
type SdkStatus = 'loading' | 'ready' | 'failed';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  // The landing page's "Get started" links here with ?mode=signup.
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<Mode>(searchParams.get('mode') === 'signup' ? 'signup' : 'signin');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    email: '',
    fullName: '',
    password: '',
    confirmPassword: '',
  });

  const loginMutation = useLogin();
  const registerMutation = useRegister();
  const socialMutation = useSocialLogin();
  // Covers the whole social sign-in: provider popup + backend call.
  const [socialPending, setSocialPending] = useState<SocialProvider | null>(null);
  const [sdkStatus, setSdkStatus] = useState<Partial<Record<SocialProvider, SdkStatus>>>({});
  const isPending = loginMutation.isPending || registerMutation.isPending || !!socialPending;

  // Load the provider SDKs up front: the popup must open synchronously on click.
  const loadSdk = (provider: SocialProvider) => {
    setSdkStatus((prev) => ({ ...prev, [provider]: 'loading' }));
    loadSocialSdk(provider).then(
      () => setSdkStatus((prev) => ({ ...prev, [provider]: 'ready' })),
      () => setSdkStatus((prev) => ({ ...prev, [provider]: 'failed' }))
    );
  };
  useEffect(() => {
    enabledSocialProviders.forEach(loadSdk);
    // Once on mount; loadSdk only calls stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isAuthenticated()) {
    return <Navigate to="/tasks" replace />;
  }

  const setField = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
  };

  const passwordsMismatch =
    mode === 'signup' && !!form.confirmPassword && form.password !== form.confirmPassword;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (mode === 'signin') {
      loginMutation.mutate(
        { email: form.email.trim(), password: form.password },
        {
          onSuccess: () => navigate('/tasks', { replace: true }),
          onError: (err) => setError(getApiErrorMessage(err, 'Unable to sign in. Check your email and password.')),
        }
      );
    } else {
      if (form.password !== form.confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      registerMutation.mutate(
        {
          email: form.email.trim(),
          full_name: form.fullName.trim() || undefined,
          password1: form.password,
          password2: form.confirmPassword,
        },
        {
          onSuccess: () => navigate('/tasks', { replace: true }),
          onError: (err) => setError(getApiErrorMessage(err, 'Unable to create your account.')),
        }
      );
    }
  };

  const handleSocialLogin = (provider: SocialProvider) => {
    const { label } = SOCIAL_BUTTONS[provider];
    setError(null);
    if (sdkStatus[provider] !== 'ready') {
      if (sdkStatus[provider] === 'failed') {
        setError(`Couldn't load ${label} sign-in. Check your connection or ad blocker, then try again.`);
        loadSdk(provider);
      }
      return;
    }

    setSocialPending(provider);
    requestSocialCredential(provider)
      .then((payload) => socialMutation.mutateAsync({ provider, payload }))
      .then(() => navigate('/tasks', { replace: true }))
      .catch((err) => {
        if (err instanceof SocialLoginCancelled) return;
        setError(
          err instanceof SocialLoginError
            ? err.message
            : getApiErrorMessage(err, `Unable to sign in with ${label}.`)
        );
      })
      .finally(() => setSocialPending(null));
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      {/* Brand panel */}
      <Box
        sx={{
          flex: 1.1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          px: { md: 8, lg: 12 },
          color: '#fff',
          background: 'linear-gradient(160deg, #312E81 0%, #4F46E5 55%, #6366F1 100%)',
        }}
      >
        <Stack
          component={RouterLink}
          to="/"
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: 6, color: 'inherit', textDecoration: 'none', alignSelf: 'flex-start' }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255,255,255,0.15)',
            }}
          >
            <BrandMark />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {BRAND.name}
          </Typography>
        </Stack>

        <Typography variant="h4" sx={{ mb: 1.5, maxWidth: 480 }}>
          Your day, organized — with an AI that does the busywork.
        </Typography>
        <Typography sx={{ mb: 6, color: 'rgba(255,255,255,0.75)', maxWidth: 440 }}>
          {BRAND.tagline}
        </Typography>

        <Stack spacing={3.5}>
          {FEATURES.map((f) => (
            <Stack key={f.title} direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(255,255,255,0.12)',
                  flexShrink: 0,
                }}
              >
                {f.icon}
              </Box>
              <Box>
                <Typography variant="subtitle1">{f.title}</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {f.text}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* Form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 440,
            p: { xs: 3, sm: 5 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 4,
          }}
        >
          <Stack
            component={RouterLink}
            to="/"
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ mb: 3, display: { xs: 'flex', md: 'none' }, color: 'inherit', textDecoration: 'none' }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'primary.main',
                color: '#fff',
              }}
            >
              <BrandMark fontSize="small" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {BRAND.name}
            </Typography>
          </Stack>

          <Typography variant="h5" gutterBottom>
            {mode === 'signin' ? 'Welcome back' : 'Create your account'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {mode === 'signin'
              ? 'Sign in to pick up where you left off.'
              : 'A few details and you are ready to go.'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2.5, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <Stack spacing={2.25}>
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={setField('email')}
                required
                fullWidth
                autoFocus
                autoComplete="email"
              />

              {mode === 'signup' && (
                <TextField
                  label="Full name (optional)"
                  value={form.fullName}
                  onChange={setField('fullName')}
                  fullWidth
                  autoComplete="name"
                />
              )}

              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={setField('password')}
                required
                fullWidth
                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                helperText={mode === 'signup' ? 'At least 8 characters, not entirely numeric.' : undefined}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((s) => !s)}
                        edge="end"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {mode === 'signup' && (
                <TextField
                  label="Confirm password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={setField('confirmPassword')}
                  required
                  fullWidth
                  autoComplete="new-password"
                  error={passwordsMismatch}
                  helperText={passwordsMismatch ? 'Passwords do not match' : ' '}
                />
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isPending || !form.email || !form.password || passwordsMismatch}
                sx={{ py: 1.4 }}
              >
                {isPending
                  ? mode === 'signin'
                    ? 'Signing in…'
                    : 'Creating account…'
                  : mode === 'signin'
                    ? 'Sign in'
                    : 'Create account'}
              </Button>
            </Stack>
          </form>

          {enabledSocialProviders.length > 0 && (
            <>
              <Divider sx={{ my: 3, typography: 'body2', color: 'text.secondary' }}>or</Divider>
              <Stack spacing={1.25}>
                {enabledSocialProviders.map((provider) => {
                  const { label, icon } = SOCIAL_BUTTONS[provider];
                  return (
                    <Button
                      key={provider}
                      variant="outlined"
                      color="inherit"
                      size="large"
                      startIcon={icon}
                      onClick={() => handleSocialLogin(provider)}
                      disabled={isPending || sdkStatus[provider] === 'loading'}
                      sx={{ py: 1.1, borderColor: 'divider', fontWeight: 600 }}
                    >
                      {socialPending === provider ? `Signing in with ${label}…` : `Continue with ${label}`}
                    </Button>
                  );
                })}
              </Stack>
            </>
          )}

          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2.5, textAlign: 'center' }}>
            By continuing, you agree to our{' '}
            <Link component={RouterLink} to="/terms" underline="hover">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link component={RouterLink} to="/privacy" underline="hover">
              Privacy Policy
            </Link>
            . If you're under 18, a parent or guardian must agree for you.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            {mode === 'signin' ? (
              <>
                New to {BRAND.name}?{' '}
                <Link component="button" type="button" onClick={() => switchMode('signup')} underline="hover">
                  Create an account
                </Link>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <Link component="button" type="button" onClick={() => switchMode('signin')} underline="hover">
                  Sign in
                </Link>
              </>
            )}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginPage;
